import React, { useEffect, useState } from "react";
import { Container, Row, Col, Button, Spinner, Alert, Table, Modal, Pagination, Form } from "react-bootstrap";
import axios from "axios";
import Swal from "sweetalert2";
import Layout from "../../../layout/default";
import Block from "../../../components/Block/Block";
import { Link } from "react-router-dom";


// const baseURL = "http://127.0.0.1:8000/storage/";
const AdminOrdersList = () => {
    const [orders, setOrders] = useState([]); // Default value as an empty array
    const [loading, setLoading] = useState(true);
    const [showViewModal, setShowViewModal] = useState(false); // For view modal
    const [viewItemDetails, setViewItemDetails] = useState([]); // To store view item details (changed to an array)
    const [currentPage, setCurrentPage] = useState(1); // Track current page
    const [totalPages, setTotalPages] = useState(1); // Track total pages

    useEffect(() => {
        fetchOrders();
    }, [currentPage]);

    const fetchOrders = async () => {
        try {
            const authToken = localStorage.getItem("authToken");
            if (!authToken) {
                Swal.fire({
                    icon: "error",
                    title: "Authentication Error",
                    text: "No authentication token found. Please log in.",
                });
                return;
            }

            const response = await axios.get(
                `${process.env.REACT_APP_HOST}api/orders/admin?page=${currentPage}&limit=10`, // Pagination with page and limit
                {
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                    },
                }
            );

            if (response.status === 200 && response.data.orders) {
                setOrders(response.data.orders || []);
                setTotalPages(response.data.totalPages || 1); // Assuming the API returns totalPages
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Fetch Error",
                    text: "Failed to fetch orders.",
                });
            }
        } catch (error) {
            console.error(error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "An error occurred while fetching orders.",
            });
        } finally {
            setLoading(false);
        }
    };

    const fetchOrderItemDetails = async (orderId) => {
        try {
            const authToken = localStorage.getItem("authToken");
            if (!authToken) {
                Swal.fire({
                    icon: "error",
                    title: "Authentication Error",
                    text: "No authentication token found. Please log in.",
                });
                return;
            }

            const response = await axios.get(
                `${process.env.REACT_APP_HOST}api/order-items/admin/${orderId}`, // Updated API endpoint
                {
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                    },
                }
            );

            if (response.status === 200 && response.data.data) {
                setViewItemDetails(response.data.data); // Set array of order items
                setShowViewModal(true);
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Fetch Error",
                    text: "Failed to fetch order item details.",
                });
            }
        } catch (error) {
            console.error(error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "An error occurred while fetching order item details.",
            });
        }
    };

    const handleStatusChange = async (orderId, newStatus) => {
        try {
            const authToken = localStorage.getItem("authToken");
            if (!authToken) {
                Swal.fire({
                    icon: "error",
                    title: "Authentication Error",
                    text: "No authentication token found. Please log in.",
                });
                return;
            }

            const response = await axios.put(
                `${process.env.REACT_APP_HOST}api/orders/status/${orderId}?order_status=${newStatus}`,
                {},
                {
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                    },
                }
            );

            if (response.status === 200) {
                fetchOrders(); // Refresh the orders list after status update
                Swal.fire({
                    icon: "success",
                    title: "Success",
                    text: "Order status updated successfully!",
                });
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Update Error",
                    text: "Failed to update order status.",
                });
            }
        } catch (error) {
            console.error(error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "An error occurred while updating the order status.",
            });
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100">
                <Spinner animation="border" />
            </div>
        );
    }

    return (
        <Layout title="Orders">
            <Block.HeadContent>
                <ul className="d-flex">
                    <li>
                        <Link
                            to="/shop/admin/admin-panel"
                            className="btn btn-primary btn-md d-md-none"
                        >
                            <span>Go To Panel</span>
                        </Link>
                    </li>
                    <li>
                        <Link
                            to="/shop/admin/admin-panel"
                            className="btn btn-primary d-none d-md-inline-flex"
                        >
                            <span>Go To Panel</span>
                        </Link>
                    </li>
                </ul>
            </Block.HeadContent>
            <Container className="mt-4">
                <h1 className="mb-4">Admin Orders List</h1>
                {Array.isArray(orders) && orders.length === 0 ? (
                    <Alert variant="info" className="text-center">
                        No orders found.
                    </Alert>
                ) : (
                    <>
                        <Table striped bordered hover responsive>
                            <thead>
                                <tr>
                                    <th>Order ID</th>
                                    <th>User ID</th>
                                    <th>Status</th>
                                    <th>Total Amount</th>
                                    <th>Discount</th>
                                    <th>Payable Amount</th>
                                    <th>Order Date</th>
                                    <th>Tracking Number</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {orders.map((order) => (
                                    <tr key={order.order_id}>
                                        <td>{order.order_id}</td>
                                        <td>{order.user_id}</td>
                                        <td>
                                            <Form.Control
                                                as="select"
                                                value={order.order_status}
                                                onChange={(e) => handleStatusChange(order.order_id, e.target.value)}
                                            >
                                                <option value={0}>Ordered</option>
                                                <option value={1}>Dispatched</option>
                                                <option value={2}>Delivered</option>
                                                <option value={3}>Cancelled</option>
                                            </Form.Control>
                                        </td>
                                        <td>₹{order.total_amount}</td>
                                        <td>₹{order.discount ? Number(order.discount).toFixed(2) : "N/A"}</td>
                                        <td>₹{order.grand_total}</td>
                                        <td>
                                            {new Date(order.OrderDate).toLocaleDateString('en-GB')}
                                        </td>

                                        <td>{order.tracking_number}</td>
                                        <td>
                                            <Button
                                                variant="info"
                                                size="sm"
                                                onClick={() => fetchOrderItemDetails(order.order_id)}
                                            >
                                                View
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>

                        {/* Pagination */}
                        <div className="d-flex justify-content-center mt-4">
                            <Pagination>
                                <Pagination.Prev
                                    onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
                                />
                                {[...Array(totalPages)].map((_, index) => (
                                    <Pagination.Item
                                        key={index + 1}
                                        active={index + 1 === currentPage}
                                        onClick={() => handlePageChange(index + 1)}
                                    >
                                        {index + 1}
                                    </Pagination.Item>
                                ))}
                                <Pagination.Next
                                    onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
                                />
                            </Pagination>
                        </div>
                    </>
                )}
            </Container>

            {/* View Modal */}
            <Modal show={showViewModal} onHide={() => setShowViewModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Order Item Details</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {viewItemDetails.length > 0 ? (
                        viewItemDetails.map((item) => (
                            <div key={item.product_id}>
                                <h5>Product Title: {item.product_title}</h5>
                                <img
                                    src={`${process.env.REACT_APP_HOST}storage/${item.image_url}`}
                                    alt={item.product_title}
                                    className="rounded me-3"
                                    style={{ width: "120px", height: "120px", objectFit: "cover" }}
                                />                                <p><strong>Product ID:</strong> {item.product_id}</p>
                                <p><strong>Quantity:</strong> {item.quantity}</p>
                                <p><strong>Price:</strong> ₹{Number(item.price).toFixed(2)}</p>
                                <p><strong>Discount:</strong> ₹{Number(item.discount).toFixed(2)}</p>
                                <p><strong>Total Amount:</strong> ₹{Number(item.total_amount).toFixed(2)}</p>
                            </div>
                        ))
                    ) : (
                        <p>Loading item details...</p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowViewModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </Layout>
    );
};

export default AdminOrdersList;
